import React, { useState } from 'react'
import { TableCell, TableRow, IconButton, Collapse, Box, Typography, Stack, Checkbox, Avatar } from '@mui/material'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { t } from 'i18next'
import { UpcomingVacationDTO } from '../../../store/types/utils/VacationTypes'
import { useGetVacationAcknowledgementQuery } from '../../../store/redux/reducers/vacationAcknowledgementApiSlice'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import DateFormat from '../../../store/types/enums/DateFormat'

dayjs.extend(utc)
dayjs.extend(timezone)

const formatUtcToLocal = (isoDate?: string | null, format = 'YYYY-MM-DD HH:mm') =>
  isoDate ? dayjs.utc(isoDate).local().format(format) : ''

type UpcomingVacationRowProps = {
  vacation: UpcomingVacationDTO
  isChecked: boolean
  onCheck: (vacationId: string) => void
  editMode: boolean
  isOwnVacation: boolean
}

const UpcomingVacationRow: React.FC<UpcomingVacationRowProps> = ({ vacation, isChecked, onCheck, editMode, isOwnVacation }) => {
  const [open, setOpen] = useState(false)
  const { data: ackData } = useGetVacationAcknowledgementQuery(vacation.vacationId, { skip: !open })
  const handleClick = () => {
    setOpen(prev => !prev)
  }
  const employeeName = `${vacation.employee.firstName} ${vacation.employee.lastName}`
  const timeSlot = `${formatUtcToLocal(vacation.displayStartDate, 'DD.MM')} - ${formatUtcToLocal(
    vacation.displayEndDate,
    'DD.MM'
  )}`

  const renderCheckOrIcon = () => {
    if (editMode) {
      return <Checkbox checked={isChecked} onChange={() => onCheck(vacation.vacationId)} disabled={isOwnVacation} />
    }
    return isChecked ? <CheckCircleIcon color="success" /> : <RadioButtonUncheckedIcon color="disabled" />
  }

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: '60px' }}>
          <IconButton size="small" onClick={handleClick}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
            <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>{vacation.employee.firstName.charAt(0)}</Avatar>
            <Typography
              sx={{
                width: '100%',
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
              }}
            >
              {employeeName}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell>{timeSlot}</TableCell>
        <TableCell align="center">{renderCheckOrIcon()}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ p: 0, borderBottom: 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack spacing={1}>
                <Typography variant="body2">
                  {`${t('components.upcomingVacation.addedAt')} ${formatUtcToLocal(
                    vacation.createdAt,
                    DateFormat.DayMonthYearTime
                  )}`}
                </Typography>
                {ackData && ackData.acknowledgements.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2">{t('components.upcomingVacation.allAcks')}</Typography>
                    {ackData.acknowledgements.map(ack => (
                      <Typography key={ack.acknowledgedDate} variant="body2">
                        <Box component="span" fontWeight="bold">
                          {ack.acknowledgedByName}
                        </Box>
                        {` ${t('components.upcomingVacation.at')} ${formatUtcToLocal(
                          ack.acknowledgedDate,
                          DateFormat.DayMonthYearTime
                        )}`}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default UpcomingVacationRow
